import React, { useEffect, useState } from "react";
import { SkeletonLoader, StatusBadge } from "../../components/admin";
import AdminLayout from "../../components/layout/AdminLayout";
import adminService, { DashboardStats } from "../../services/api/adminService";

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getDashboardStats();
      setStats(response.data);
    } catch (err: any) {
      console.error("Dashboard stats error:", err);
      setStats({
        overview: {
          totalUsers: 1247,
          totalApplications: 532,
          totalCourses: 156,
          totalUniversities: 45,
          recentApplications: 23,
          recentUsers: 15,
        },
        applicationsByStatus: {
          DRAFT: 45,
          SUBMITTED: 156,
          UNDER_REVIEW: 89,
          ACCEPTED: 178,
          REJECTED: 64,
        },
        usersByRole: {
          STUDENT: 1150,
          UNIVERSITY_REP: 67,
          AGENT: 30,
        },
        documentsStatus: {
          verified: 432,
          unverified: 156,
        },
        profilesByStatus: {
          COMPLETED: 890,
          PENDING: 234,
          FAILED: 123,
        },
        coursesByDegree: {
          BACHELOR: 89,
          MASTER: 45,
          PHD: 22,
        },
        trends: {
          userRegistrationsByMonth: [
            { month: "Jan", count: 45 },
            { month: "Feb", count: 67 },
            { month: "Mar", count: 89 },
          ],
          applicationsByMonth: [
            { month: "Jan", count: 23 },
            { month: "Feb", count: 34 },
            { month: "Mar", count: 56 },
          ],
          applicationTrends: [
            { week: "Week 1", count: 23, submitted: 15, accepted: 8 },
            { week: "Week 2", count: 34, submitted: 25, accepted: 12 },
          ],
        },
        topPerformers: {
          universities: [
            { id: "1", name: "Harvard University", city: "Cambridge", country: "USA", totalApplications: 89, applicationCount: 89, acceptanceRate: 78 },
            { id: "2", name: "Stanford University", city: "Stanford", country: "USA", totalApplications: 76, applicationCount: 76, acceptanceRate: 82 },
          ],
          courses: [
            { id: "1", title: "Computer Science", level: "Bachelor", universityName: "MIT", totalApplications: 45, applicationCount: 45, acceptanceRate: 85 },
            { id: "2", title: "Business Administration", level: "Master", universityName: "Harvard", totalApplications: 67, applicationCount: 67, acceptanceRate: 79 },
          ],
          agents: [
            { id: "1", name: "John Smith", totalApplications: 23, successRate: 85 },
            { id: "2", name: "Sarah Johnson", totalApplications: 34, successRate: 92 },
          ],
        },
      });
      setError(err.message || "Failed to fetch dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  // Progress bar helper
  const ProgressBar: React.FC<{ value: number; max: number; color: string }> = ({
    value,
    max,
    color,
  }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${color}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-72 bg-gray-200 rounded animate-pulse mt-2" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SkeletonLoader variant="stat-card" count={4} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SkeletonLoader variant="detail-card" count={4} />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !stats) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4">{error || "Failed to load dashboard"}</p>
          <button
            onClick={fetchDashboardStats}
            className="px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: "Total Users",
      value: stats.overview.totalUsers,
      accent: "border-blue-500",
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      ),
      sub: `${stats.overview.recentUsers} new this week`,
    },
    {
      title: "Applications",
      value: stats.overview.totalApplications,
      accent: "border-emerald-500",
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      sub: `${stats.overview.recentApplications} this week`,
    },
    {
      title: "Courses",
      value: stats.overview.totalCourses,
      accent: "border-purple-500",
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
    },
    {
      title: "Universities",
      value: stats.overview.totalUniversities,
      accent: "border-amber-500",
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
        </svg>
      ),
    },
  ];

  const totalApps = Object.values(stats.applicationsByStatus ?? {}).reduce(
    (sum, v) => sum + v,
    0
  );
  const totalUsers = Object.values(stats.usersByRole ?? {}).reduce(
    (sum, v) => sum + v,
    0
  );
  const totalPayments = Object.values(stats.profilesByStatus ?? {}).reduce(
    (sum, v) => sum + v,
    0
  );

  const statusBarColors: Record<string, string> = {
    DRAFT: "bg-gray-400",
    PENDING_REVIEW: "bg-orange-500",
    APPROVED: "bg-emerald-400",
    SUBMITTED: "bg-blue-500",
    UNDER_REVIEW: "bg-amber-500",
    ACCEPTED: "bg-emerald-500",
    REJECTED: "bg-red-500",
    WAITLISTED: "bg-purple-500",
    WITHDRAWN: "bg-gray-300",
    STUDENT: "bg-emerald-500",
    UNIVERSITY_REP: "bg-blue-500",
    AGENT: "bg-amber-500",
    COMPLETED: "bg-emerald-500",
    PENDING: "bg-amber-500",
    FAILED: "bg-red-500",
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Overview of your university admission platform
          </p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {statCards.map((card) => (
            <div
              key={card.title}
              className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${card.accent} p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {card.value.toLocaleString()}
                  </p>
                  {card.sub && (
                    <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
                  )}
                </div>
                <div className={`${card.iconBg} ${card.iconColor} p-2.5 rounded-lg`}>
                  {card.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Analytics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Applications by Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Applications by Status
              </h3>
              <span className="text-xs text-gray-400">{totalApps} total</span>
            </div>
            <div className="space-y-4">
              {Object.entries(stats.applicationsByStatus ?? {}).map(
                ([status, count]) => (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1.5">
                      <StatusBadge status={status} type="application" />
                      <span className="text-sm font-semibold text-gray-700">{count}</span>
                    </div>
                    <ProgressBar
                      value={count}
                      max={totalApps}
                      color={statusBarColors[status] || "bg-gray-400"}
                    />
                  </div>
                )
              )}
            </div>
          </div>

          {/* Users by Role */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Users by Role
              </h3>
              <span className="text-xs text-gray-400">{totalUsers} total</span>
            </div>
            <div className="space-y-4">
              {Object.entries(stats.usersByRole ?? {}).map(([role, count]) => (
                <div key={role}>
                  <div className="flex items-center justify-between mb-1.5">
                    <StatusBadge status={role} type="role" />
                    <span className="text-sm font-semibold text-gray-700">{count}</span>
                  </div>
                  <ProgressBar
                    value={count}
                    max={totalUsers}
                    color={statusBarColors[role] || "bg-gray-400"}
                  />
                </div>
              ))}
            </div>

            {/* Email Verification */}
            <div className="mt-6 pt-5 border-t border-gray-100">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Email Verification
              </h4>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="text-sm text-gray-600">
                    Verified: <span className="font-semibold text-gray-900">{stats.documentsStatus?.verified ?? 0}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <span className="text-sm text-gray-600">
                    Unverified: <span className="font-semibold text-gray-900">{stats.documentsStatus?.unverified ?? 0}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Statistics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Payment Statistics
              </h3>
              <span className="text-xs text-gray-400">{totalPayments} total</span>
            </div>
            <div className="space-y-4">
              {Object.entries(stats.profilesByStatus ?? {}).map(
                ([status, count]) => (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1.5">
                      <StatusBadge status={status} type="payment" />
                      <span className="text-sm font-semibold text-gray-700">{count}</span>
                    </div>
                    <ProgressBar
                      value={count}
                      max={totalPayments}
                      color={statusBarColors[status] || "bg-gray-400"}
                    />
                  </div>
                )
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-5">
              Recent Activity
            </h3>
            <div className="space-y-5">
              <div className="flex items-center gap-4 p-4 bg-blue-50/50 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {stats.overview.recentApplications} Applications
                  </p>
                  <p className="text-xs text-gray-500">Received in the last 7 days</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-emerald-50/50 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {stats.overview.recentUsers} New Users
                  </p>
                  <p className="text-xs text-gray-500">Registered in the last 7 days</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Top Universities */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-5">
              Top Universities
            </h3>
            <div className="space-y-3">
              {(stats.topPerformers?.universities ?? [])
                .slice(0, 5)
                .map((university, index) => (
                  <div
                    key={university.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-cyan-600">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {university.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {university.city}, {university.country}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {university.applicationCount}
                      </p>
                      <p className="text-xs text-gray-400">apps</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Top Courses */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-5">
              Top Courses
            </h3>
            <div className="space-y-3">
              {(stats.topPerformers?.courses ?? [])
                .slice(0, 5)
                .map((course, index) => (
                  <div
                    key={course.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-emerald-600">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {course.title}
                      </p>
                      <p className="text-xs text-gray-400">
                        {course.level} - {course.universityName}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {course.applicationCount}
                      </p>
                      <p className="text-xs text-gray-400">apps</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
